import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { FiUser, FiCheckCircle, FiXCircle, FiClock, FiInstagram, FiMessageSquare, FiTrash2, FiSearch, FiExternalLink } from 'react-icons/fi';
import { SiTiktok } from 'react-icons/si';
import Swal from 'sweetalert2';
import { adminApi } from '../../utils/api';
import './AdminPages.css';

const AdminPendingApplicationsPage = () => {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [selectedApp, setSelectedApp] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageData, setMessageData] = useState({ subject: '', content: '' });

  const { data: applicationsData, isLoading } = useQuery(
    ['admin-pending-applications', page, searchTerm],
    () => adminApi.getApplications({
      page,
      limit: 50,
      status: 'pending',
      search: searchTerm || undefined
    }),
    { keepPreviousData: true, refetchInterval: 30000 }
  );

  const updateStatusMutation = useMutation(
    ({ id, status, notes }) => adminApi.updateApplicationStatus(id, status, notes),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('admin-pending-applications');
        queryClient.invalidateQueries('admin-stats');
        queryClient.invalidateQueries('user-stats');
        setSelectedApp(null);
        setAdminNotes('');
      }
    }
  );

  const sendMessageMutation = useMutation(
    (data) => adminApi.sendMessage(data),
    {
      onSuccess: () => {
        setShowMessageModal(false);
        setMessageData({ subject: '', content: '' });
        Swal.fire({
          title: 'נשלח!',
          text: 'ההודעה נשלחה בהצלחה',
          icon: 'success',
          confirmButtonColor: '#F7941D',
          confirmButtonText: 'אישור'
        });
      },
      onError: (error) => {
        Swal.fire({
          title: 'שגיאה!',
          text: error.response?.data?.error || 'שגיאה בשליחת ההודעה',
          icon: 'error',
          confirmButtonColor: '#F7941D',
          confirmButtonText: 'אישור'
        });
      }
    }
  );

  const deleteApplicationMutation = useMutation(
    (id) => adminApi.deleteApplication(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('admin-pending-applications');
        queryClient.invalidateQueries('admin-stats');
        Swal.fire({
          title: 'נמחק!',
          text: 'ההגשה נמחקה בהצלחה',
          icon: 'success',
          confirmButtonColor: '#F7941D',
          confirmButtonText: 'אישור'
        });
      }
    }
  );

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchTerm(searchInput);
    setPage(1);
  };

  const clearSearch = () => {
    setSearchInput('');
    setSearchTerm('');
    setPage(1);
  };

  const handleDelete = (app) => {
    Swal.fire({
      title: 'למחוק את ההגשה?',
      text: `הגשה של ${app.display_name || app.user_name || app.full_name} לקמפיין ${app.campaign_title}`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'מחק',
      cancelButtonText: 'ביטול'
    }).then((result) => {
      if (result.isConfirmed) {
        deleteApplicationMutation.mutate(app.id);
      }
    });
  };

  const handleApprove = (app) => {
    setSelectedApp(app);
    setAdminNotes('');
  };

  const handleReject = (app) => {
    setSelectedApp(app);
    setAdminNotes('');
  };

  const confirmStatusChange = (status) => {
    if (!selectedApp) return;
    updateStatusMutation.mutate({
      id: selectedApp.id,
      status,
      notes: adminNotes
    });
  };

  const openMessageModal = (app) => {
    setSelectedApp(app);
    setMessageData({
      subject: `בנוגע להגשה שלך לקמפיין ${app.campaign_title}`,
      content: ''
    });
    setShowMessageModal(true);
  };

  const sendMessage = () => {
    if (!selectedApp || !messageData.content) return;
    sendMessageMutation.mutate({
      receiverId: selectedApp.user_id,
      campaignId: selectedApp.campaign_id,
      subject: messageData.subject,
      content: messageData.content
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('he-IL');
  };

  const applications = applicationsData?.data?.applications || [];
  const pagination = applicationsData?.data?.pagination || {};

  return (
    <div className="admin-page">
      <header className="admin-header">
        <div className="header-content">
          <h1>הגשות ממתינות לאישור</h1>
          <span className="total-count">{pagination.total || 0} {(pagination.total || 0) === 1 ? 'הגשה' : 'הגשות'}</span>
        </div>
      </header>

      <div className="admin-toolbar">
        <form className="search-form" onSubmit={handleSearch}>
          <div className="search-input-wrapper">
            <FiSearch className="search-icon" />
            <input
              type="text"
              placeholder="חיפוש לפי שם, אימייל או שם משתמש..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>
          <button type="submit" className="btn btn-primary btn-sm">חפש</button>
          {searchTerm && (
            <button type="button" className="btn btn-outline btn-sm" onClick={clearSearch}>נקה</button>
          )}
        </form>
      </div>

      {isLoading ? (
        <div className="loading-state">
          <div className="spinner" />
          <p>טוען הגשות...</p>
        </div>
      ) : applications.length === 0 ? (
        <div className="empty-state">
          <FiCheckCircle className="empty-icon" />
          <p>אין הגשות ממתינות כרגע — הכל בוצע!</p>
        </div>
      ) : (
        <div className="table-container">
          <table className="admin-table">
            <thead>
              <tr>
                <th>שם משפיען</th>
                <th>אימייל</th>
                <th>טלפון</th>
                <th>קמפיין</th>
                <th>אינסטגרם</th>
                <th>טיקטוק</th>
                <th>תאריך הגשה</th>
                <th>פעולות</th>
              </tr>
            </thead>
            <tbody>
              {applications.map((app) => (
                <tr key={app.id}>
                  <td data-label="שם משפיען">
                    <span className="user-name-cell">
                      {app.display_name || app.user_name || app.full_name || '-'}
                    </span>
                  </td>
                  <td data-label="אימייל">{app.user_email || app.email || '-'}</td>
                  <td data-label="טלפון">
                    {app.user_phone ? (
                      <a href={`tel:${app.user_phone}`} className="phone-link">{app.user_phone}</a>
                    ) : '-'}
                  </td>
                  <td data-label="קמפיין">
                    {app.campaign_id ? (
                      <Link to={`/campaigns/${app.campaign_id}`} className="phone-link">
                        {app.campaign_title || '-'} <FiExternalLink style={{ verticalAlign: 'middle' }} />
                      </Link>
                    ) : (app.campaign_title || '-')}
                  </td>
                  <td data-label="אינסטגרם">
                    {app.instagram_username ? (
                      <a
                        href={`https://instagram.com/${app.instagram_username}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="social-link instagram"
                      >
                        <FiInstagram /> {app.instagram_username}
                      </a>
                    ) : '-'}
                  </td>
                  <td data-label="טיקטוק">
                    {app.tiktok_username ? (
                      <a
                        href={`https://tiktok.com/@${app.tiktok_username}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="social-link tiktok"
                      >
                        <SiTiktok /> {app.tiktok_username}
                      </a>
                    ) : '-'}
                  </td>
                  <td data-label="תאריך הגשה">{formatDate(app.created_at)}</td>
                  <td data-label="פעולות">
                    <div className="actions-cell">
                      <button
                        className="btn btn-sm btn-success"
                        onClick={() => handleApprove(app)}
                        title="אשר"
                      >
                        <FiCheckCircle />
                      </button>
                      <button
                        className="btn btn-sm btn-error"
                        onClick={() => handleReject(app)}
                        title="דחה"
                      >
                        <FiXCircle />
                      </button>
                      <button
                        className="btn btn-sm btn-outline"
                        onClick={() => openMessageModal(app)}
                        title="שלח הודעה"
                      >
                        <FiMessageSquare />
                      </button>
                      <button
                        className="btn btn-sm btn-error"
                        onClick={() => handleDelete(app)}
                        title="מחק"
                      >
                        <FiTrash2 />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {pagination.pages > 1 && (
        <div className="pagination">
          <button
            className="btn btn-outline"
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
          >
            הקודם
          </button>
          <span className="page-info">
            עמוד {page} מתוך {pagination.pages}
          </span>
          <button
            className="btn btn-outline"
            disabled={page === pagination.pages}
            onClick={() => setPage(page + 1)}
          >
            הבא
          </button>
        </div>
      )}

      <AnimatePresence>
        {selectedApp && !showMessageModal && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedApp(null)}
          >
            <motion.div
              className="modal"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <h2>עדכון סטטוס הגשה</h2>
              <p>משפיען: <strong>{selectedApp.display_name || selectedApp.user_name || selectedApp.full_name}</strong></p>
              <p>קמפיין: <strong>{selectedApp.campaign_title}</strong></p>

              <div className="form-group">
                <label>הערות מנהל (אופציונלי)</label>
                <textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="הוסף הערות..."
                  rows={3}
                />
              </div>

              <div className="modal-actions">
                <button
                  className="btn btn-success"
                  onClick={() => confirmStatusChange('approved')}
                  disabled={updateStatusMutation.isLoading}
                >
                  <FiCheckCircle /> אשר
                </button>
                <button
                  className="btn btn-error"
                  onClick={() => confirmStatusChange('rejected')}
                  disabled={updateStatusMutation.isLoading}
                >
                  <FiXCircle /> דחה
                </button>
                <button className="btn btn-outline" onClick={() => setSelectedApp(null)}>ביטול</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showMessageModal && selectedApp && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowMessageModal(false)}
          >
            <motion.div
              className="modal"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <h2>שלח הודעה</h2>
              <p>אל: <strong>{selectedApp.display_name || selectedApp.user_name || selectedApp.full_name}</strong></p>

              <div className="form-group">
                <label>נושא</label>
                <input
                  type="text"
                  value={messageData.subject}
                  onChange={(e) => setMessageData({ ...messageData, subject: e.target.value })}
                  placeholder="נושא ההודעה"
                />
              </div>

              <div className="form-group">
                <label>תוכן ההודעה</label>
                <textarea
                  value={messageData.content}
                  onChange={(e) => setMessageData({ ...messageData, content: e.target.value })}
                  placeholder="כתוב את ההודעה..."
                  rows={5}
                />
              </div>

              <div className="modal-actions">
                <button
                  className="btn btn-primary"
                  onClick={sendMessage}
                  disabled={!messageData.content || sendMessageMutation.isLoading}
                >
                  <FiMessageSquare /> שלח
                </button>
                <button className="btn btn-outline" onClick={() => setShowMessageModal(false)}>ביטול</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminPendingApplicationsPage;
